import React from 'react';
import clsx from 'clsx';
import {faEllipsisV} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {
	getTableHeaderCellResizerUtilityClass,
	TABLE_HEADER_CELL_RESIZER_CLASS_NAME,
	tableHeaderCellResizerClasses,
	type TableHeaderCellResizerClasses,
} from './tableHeaderCellResizerClasses';

type TableHeaderCellResizerUtilityClassProps = WithClasses<
	TableHeaderCellResizerClasses,
	{
		active?: boolean;
	}
>;

const useUtilityClasses = (
	ownerState: TableHeaderCellResizerUtilityClassProps,
) => {
	const {classes, active} = ownerState;

	const slots = {
		root: ['root', active && 'active'],
		ellipsis: ['ellipsis'],
	};

	return composeClasses(slots, getTableHeaderCellResizerUtilityClass, classes);
};

const TableHeaderCellResizerRoot = styled('div', {
	name: TABLE_HEADER_CELL_RESIZER_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	...theme.mixins.absoluteFill,
	alignItems: 'center',
	background: theme.palette.custom.background.accent,
	borderRight: '1px solid transparent',
	cursor: 'col-resize',
	display: 'flex',
	justifyContent: 'center',
	left: 'initial',
	opacity: 0,
	overflow: 'hidden',
	transition: 'opacity 0.3s ease',
	userSelect: 'none',
	width: 8,
	zIndex: 2,
	'&:hover': {
		borderColor: theme.palette.secondary.main,
	},
}));

const TableHeaderCellResizerEllipsis = styled('div', {
	name: TABLE_HEADER_CELL_RESIZER_CLASS_NAME,
	slot: 'Ellipsis',
})(({theme}) => ({
	color: theme.palette.text.secondary,
	fontSize: 12,
}));

export interface TableHeaderCellResizerProps
	extends WithClasses<
		TableHeaderCellResizerClasses,
		React.DetailedHTMLProps<
			React.HTMLAttributes<HTMLDivElement>,
			HTMLDivElement
		>
	> {}

export const TableHeaderCellResizerPrimitive = ({
	classes: _classes,
	className,
	...props
}: TableHeaderCellResizerProps) => {
	const classes = useUtilityClasses({
		classes: _classes,
		className,
	});

	return (
		<TableHeaderCellResizerRoot
			aria-hidden="true"
			className={clsx(classes.root, className)}
			{...props}
		>
			<TableHeaderCellResizerEllipsis className={classes.ellipsis}>
				<FontAwesomeIcon icon={faEllipsisV} fixedWidth />
			</TableHeaderCellResizerEllipsis>
		</TableHeaderCellResizerRoot>
	);
};

export const TableHeaderCellResizer = React.memo(
	TableHeaderCellResizerPrimitive,
) as typeof TableHeaderCellResizerPrimitive;

export default TableHeaderCellResizer;
